/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import _forEach from 'lodash/forEach';
import bnd3Service from '../../../services/bnd3.service';
import utilityService from '../../../services/utility.service';
import ChartConst from '../../../constants/chart.constant';
import AppConst from '../../../constants/app.constant';
import WeatherConst from '../../../constants/weather.constant';
import bnChartDataService from '../../../services/chartData.service';
import d3 from '../../../globalexports/d3.export';
import { routerReplaceInterface } from '../../../services/router.interface';

function xAxisService() {
  const content = {};
  content.drawXaxis = (
    sourceData,
    flightRuleStrip,
    flightWeatherStrip,
    flightWeatherStripHeight,
    chartObj,
    xAxisTopContainerHeight,
    xAxisBottomContainerHeight,
    width,
    x,
    dateFormat,
    dateFormat2,
    flightDots,
    hideBottomAxis,
    yAxisRectWidth,
    majorContainerHeight,
    majorContainer,
    settings,
    isTopAxis,
    softDraw,
  ) => {
    const normalTextHeight = bnd3Service.getSelectionHeight(d3.select('#bntextrefroute'));
    // var questionMarkHeight = parseFloat(d3.select("#bntextrefroute").style("font-size"));
    let airportStop;
    let flightRule;
    let weatherConditions;
    let dayNightIndicatorHeight;
    const rectRadius = 2;
    if (!sourceData) {
      return;
    }

    // Setting Domain for X-axis
    x.domain(d3.extent(sourceData, (d) => d.arrival_time));
    const dayNightIndicator = chartObj.append('g')
      .attr('id', 'day-night-indicator-route')
      .attr('class', 'day-night-indicator-route')
      .attr('transform', () => `translate(${0},  ${isTopAxis
        ? 2
        : (0)})`);

    function writeAirportName(yValue) {
      // writing airport name
      airportStop
        .append('text').text((d) => d.icao_id)
        .on('click', (d) => {
          routerReplaceInterface({
            name: 'app.dashboard',
            query: {
              start: d.icao_id,
              product: 'MAV',
              refresh: true,
            },
          });
        })
        .attr('class', 'airport-stop-id airport-stop-id-link')
        .attr('y', yValue);
    }

    function writeCrossTrackNauticalMiles(yValue) {
      // writing CrossTrack
      airportStop
        .append('text').text((d) => {
          if (d.icao_id !== settings.start && d.icao_id !== settings.mid1 && d.icao_id
            !== settings.mid2 && d.icao_id !== settings.end) {
            return utilityService.roundToTenth(d.crosstrack_nm, 0) + ChartConst.crosstrackUnit;
          }
          return '';
        })
        .attr('style', 'text-anchor: middle')
        .attr('y', yValue);
    }

    function writeArrivalTime(yValue) {
      // Writing arrival time
      airportStop.append('text')
        .text((d) => dateFormat(d.arrival_time))
        .attr('style', 'text-anchor: middle')
        .attr('y', yValue);
    }

    function writeElapsedNauticalMiles(yValue) {
      // writing elapsed nautical miles
      airportStop.append('text').text((d) => {
        const nmFormat = d3.format('.0f');
        return `${nmFormat(d.elapsed_nm)}nm`;
      })
        .attr('style', 'text-anchor: middle')
        .attr('y', yValue);
    }
    const sourceForDateStrip = {
      uniqueDays: [],
      sourceData: [],
      zeroLineSource: [],
    };
    _forEach(sourceData, (airport) => {
      if (sourceForDateStrip.uniqueDays.indexOf(dateFormat2(airport.arrival_time)) < 0) {
        const temp = {
          display: dateFormat2(airport.arrival_time),
          dateValue: airport.arrival_time,
        };
        const temp1 = utilityService.clone(temp);
        let formattedDate = dateFormat(temp1.dateValue);
        formattedDate = formattedDate.replace(':', '');
        let minutesLag = 0;
        if (formattedDate.substring(0, 2) === '00') {
          minutesLag = parseInt(formattedDate.substring(2, 4), 10);
          temp1.dateValue = utilityService.subtractMinutes(temp1.dateValue, minutesLag);
        }
        sourceForDateStrip.uniqueDays.push(dateFormat2(airport.arrival_time));
        sourceForDateStrip.sourceData.push(utilityService.clone(temp1));

        const temp2 = utilityService.clone(temp);
        if (settings.timeZone === AppConst.timeZones.local.key) {
          sourceForDateStrip.zeroLineSource.push(temp2.dateValue.setHours(0, 0, 0, 0));
        } else if (formattedDate.substring(0, 2) === '00') {
          // TBD: Improve this logic. Not sure why with ZULU setting hours to 0000 not working
          // var formattedDate = dateFormat(temp2.dateValue);
          // var minutesLag = parseInt(formattedDate.substring(2, 4));
          sourceForDateStrip.zeroLineSource.push(utilityService.subtractMinutes(temp2
            .dateValue, minutesLag));
        }
      }
    });
    if (isTopAxis) {
      const zeroLines = majorContainer.append('g').selectAll('majorDaysLines')
        .data(sourceForDateStrip.zeroLineSource)
        .enter();
      zeroLines.append('line')
        .attr('class', 'zeroLine')
        .attr('x1', (d) => {
          const xValue = x(d);
          if (xValue > yAxisRectWidth) {
            return xValue;
          }
          return -200;
        })
        .attr('y1', -normalTextHeight)
        .attr('x2', (d) => {
          const xValue = x(d);
          if (xValue > yAxisRectWidth) {
            return xValue;
          }
          return -200;
        })
        .attr('y2', majorContainerHeight);
      const routeMajorDates = chartObj.append('g')
        .attr('id', 'route-major-date-strip')
        .selectAll('majorDays')
        .data(sourceForDateStrip.sourceData)
        .enter();
      routeMajorDates.append('text').text((d) => d.display).attr('id', (...args) => `routeMajorDayText${args[1]}`).attr('style', 'font-weight: bold')
        .attr('x', (...args) => {
          const d = args[0];
          const key = args[1];
          const lastXOfAxis = width - 0;
          let dayStart = (key === 0
            ? x(d.dateValue) - Math.floor(yAxisRectWidth)
            : x(d.dateValue));
          dayStart = Math.max(dayStart, 0);
          let dayEnd = lastXOfAxis;
          const selfEle = utilityService.getSelfElement(args);
          const widthOfDayText = bnd3Service.getSelectionWidth(d3.select(selfEle));
          if (sourceForDateStrip.sourceData[key + 1]) {
            dayEnd = Math.min(x(sourceForDateStrip.sourceData[key + 1].dateValue), lastXOfAxis);
            // console.log(dayStart, dayEnd, sourceForDateStrip.sourceData[key + 1].dateValue, key);
          }
          // console.log(dayStart);
          dayEnd -= ((key + 1) * 2);
          const availablespace = (dayEnd - dayStart);
          if (availablespace > widthOfDayText) {
            return dayStart + (availablespace - widthOfDayText) / 2;
          }
          return -200;
        })
        .attr('y', (isTopAxis
          ? -1
          : (normalTextHeight * 2) + 2));
    }
    // X-Axis strip
    const airportStrip = chartObj.append('g')
      .selectAll('airportsgroup')
      .data(sourceData)
      .enter();
    airportStop = airportStrip.append('g')
      .attr('class', (d) => `${'airport-stop airportStop-'}${d.icao_id}`)
      .attr('id', (d) => `airportStop-${d.icao_id}`).attr('transform', (d) => {
        const xValue = x(d.arrival_time);
        return `translate(${xValue}, ${isTopAxis
          ? normalTextHeight
          : (-normalTextHeight)})`;
      });
    // This rectangle by default is invisible i.e. opacity:0
    // This rect will be shown when tooltip is shown as dark background
    airportStop.append('rect')
      .attr('class', 'airport-stop-rect')
      .attr('rx', rectRadius)
      .attr('ry', rectRadius);

    function drawTooltipRectangle(tooltipRectHeight) {
      // No need to draw background rectangle of xAxis as they are used for tooltips
      if (!softDraw) {
        chartObj.selectAll('.airport-stop rect')
          .attr('height', () => tooltipRectHeight + 2)
          .attr('width', (...args) => {
            const selfEle = utilityService.getSelfElement(args);
            return bnd3Service.getMaxTextWidth(d3.select(selfEle.parentNode.parentNode)) + 4;
          })
          .attr('transform', (...args) => {
            const selfEle = utilityService.getSelfElement(args);
            const xValue = bnd3Service.getMaxTextWidth(d3.select(selfEle.parentNode
              .parentNode)) + 4;
            const y = (-((isTopAxis
              ? normalTextHeight
              : -normalTextHeight) - 2));
            return `translate(${-(xValue / 2)}, ${y})`;
          });
      }
    }
    const daysForDayNight = bnChartDataService.getDaysInData(
      sourceData,
      'arrival_time',
      'arrival_time',
    );
    if (isTopAxis) {
      dayNightIndicatorHeight = xAxisTopContainerHeight + flightWeatherStripHeight
        - normalTextHeight;
      bnd3Service.drawDayNightIndicator(
        dayNightIndicatorHeight,
        dayNightIndicator,
        x,
        width,
        daysForDayNight,
      );
      if (!hideBottomAxis) {
        writeArrivalTime(0);
        // writeElapsedNauticalMiles(normalTextHeight);
        // writeCrossTrackNauticalMiles(normalTextHeight * 2);
      }
      writeArrivalTime(0);
      writeAirportName(normalTextHeight);

      drawTooltipRectangle(normalTextHeight * 2);

      flightRuleStrip.append('rect')
        .attr('class', 'flight-rule-rect')
        .attr('x', 0)
        .attr('y', -((flightDots.radius * 2) + (flightDots.strokeWidth * 2)
          + 2)) // Adding 2 to have some clear space
        .attr('width', width)
        .attr('height', (flightDots.radius * 2) + (flightDots.strokeWidth * 2)
          + 2); // Adding 2 to have some clear space

      weatherConditions = flightWeatherStrip.selectAll('path')
        .data(sourceData)
        .enter();

      weatherConditions.append('svg:image')
        .attr('width', flightWeatherStripHeight)
        .attr('y', -flightWeatherStripHeight)
        .attr('x', (d) => x(d.arrival_time) - (flightWeatherStripHeight / 2))
        .attr('height', flightWeatherStripHeight)
        .attr('xlink:href', (d) => {
          // Keep it blank if weatherRepresentation is empty
          if (d.weatherRepresentation) {
            return utilityService.getWeatherFilePath(d.weatherRepresentation.filename);
          }
          return WeatherConst.noWeatherIcon;
        });

      flightRule = flightRuleStrip.selectAll('path')
        .data(sourceData)
        .enter();

      flightRule.append('path')
        .attr('fill', (d) => bnd3Service.getFlightDotColorForRruleset(d.frlServerValue || d.frl))
        .attr('stroke-width', flightDots
          .strokeWidth) // Using windSpeed.light for configuring barbs
        .attr('d', (d) => {
          // Arc to create path value for filling areas accourding to cloud
          const arc = d3.arc()
            .innerRadius(0)
            .outerRadius(flightDots.radius)
            .startAngle(0)
            .endAngle(bnd3Service.getEndAngle(d.cld))();
          return arc;
        })
        .attr('transform', (d) => {
          const xValue = x(d.arrival_time);
          const yValue = -(flightWeatherStripHeight / 2);
          return `translate(${xValue}, ${yValue})`;
        });
      flightRule.append('circle')
        .attr('cx', (d) => x(d.arrival_time))
        .attr('cy', () => -(flightWeatherStripHeight / 2))
        .attr('r', flightDots.radius)
        .attr('stroke', (d) => bnd3Service.getFlightDotColorForRruleset(d.frlServerValue || d
          .frl))
        .attr('stroke-width', flightDots.strokeWidth)
        .style('display', (d) => {
          if (!d.cld && bnd3Service.getFlightDotColorForRruleset(d.frlServerValue || d.frl)
            === 'gray') {
            return 'none';
          }
          return 'inherit';
        })
        .attr('fill', (d) => {
          if (!d.cld && bnd3Service.getFlightDotColorForRruleset(d.frlServerValue || d.frl)
            === 'gray') {
            return bnd3Service.getFlightDotColorForRruleset(d.frlServerValue || d.frl);
          }
          return 'none';
        });
      const questionWidth = (flightDots.radius * 2) + (flightDots.strokeWidth);
      flightRule.append('svg:image')
        .attr('y', () => -(questionWidth + ((flightWeatherStripHeight - questionWidth) / 2)))
        .attr('x', (d) => x(d.arrival_time) - (questionWidth / 2))
        .attr('width', questionWidth)
        .attr('height', questionWidth)
        .style('display', (d) => {
          if (!d.cld && bnd3Service.getFlightDotColorForRruleset(d.frlServerValue || d.frl)
            === 'gray') {
            return 'inherit';
          }
          return 'none';
        })
        .attr('xlink:href', () => 'static/img/dot.svg');
    } else {
      dayNightIndicatorHeight = xAxisBottomContainerHeight;
      bnd3Service.drawDayNightIndicator(
        dayNightIndicatorHeight,
        dayNightIndicator,
        x,
        width,
        daysForDayNight,
      );
      // writeAirportName(0);
      writeElapsedNauticalMiles(normalTextHeight * 2);
      writeCrossTrackNauticalMiles(normalTextHeight * 3);
      drawTooltipRectangle(!hideBottomAxis
        ? (normalTextHeight * 2)
        : normalTextHeight);
    }
  };
  return content;
}
export default xAxisService();
